import React, { useState } from 'react';
import { 
  CommandLineIcon, 
  ShieldCheckIcon, 
  CpuChipIcon, 
  ArrowPathIcon,
  SparklesIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const App = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'devops',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const dataToSend = {
      ...formData,
      recipientEmail: 'shajithkhanafridi@gmail.com',
    };

    try {
      const response = await fetch('https://formanah.alifbay.in/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        setSubmitMessage('🎉 You are on the VIP waitlist! We will reach out with early CLI and Desktop access.');
        setFormData({ name: '', email: '', role: 'devops', message: '' });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSubmitMessage(`Submission failed: ${errorData.message || 'Please try again.'}`);
      }
    } catch (error) {
      setSubmitMessage('Thank you! Your request has been recorded.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between overflow-x-hidden font-sans selection:bg-purple-500 selection:text-white">
      {/* Background ambient lighting */}
      <div className="absolute top-0 -left-20 w-96 h-96 bg-purple-600/30 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute top-40 right-0 w-[30rem] h-[30rem] bg-blue-600/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-10 left-1/3 w-[36rem] h-[36rem] bg-indigo-600/20 rounded-full blur-3xl pointer-events-none"></div>

      {/* Navigation */}
      <header className="relative z-10 max-w-6xl mx-auto w-full px-6 py-6 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-xl shadow-lg shadow-purple-500/20">
            <CpuChipIcon className="h-6 w-6 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-300">
            Talk To Your K8s
          </span>
        </div>
        <div className="flex items-center space-x-3">
          <span className="hidden sm:inline-flex items-center space-x-1.5 px-3 py-1 bg-purple-500/10 border border-purple-500/30 rounded-full text-xs font-medium text-purple-300">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            <span>CLI & Desktop App v1.1</span>
          </span>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 max-w-6xl mx-auto w-full px-6 py-8 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left column: Value Proposition & Live Terminal */}
        <div className="lg:col-span-7 space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center space-x-2 px-3 py-1 bg-slate-900 border border-slate-800 rounded-full text-xs text-slate-300">
              <SparklesIcon className="h-4 w-4 text-yellow-400" />
              <span>Zero-Trust AI SRE & Kubernetes Co-Pilot</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white">
              Stop memorizing <code className="text-purple-400 font-mono">kubectl</code>.<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-blue-400 to-indigo-300">
                Talk to your cluster instead.
              </span>
            </h1>
            <p className="text-lg text-slate-300 max-w-xl leading-relaxed">
              Translate natural language into precise, safe <code className="bg-slate-800 px-1.5 py-0.5 rounded text-purple-300">kubectl</code> commands. 
              Autonomous multi-step root cause analysis, zero-trust credentials, and safe guardrails for your terminal & desktop.
            </p>
          </div>

          {/* Interactive Terminal Demo Mockup */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/90 shadow-2xl overflow-hidden backdrop-blur-sm">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-950/80 border-b border-slate-800">
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
              </div>
              <span className="text-xs font-mono text-slate-400">kubectl-chat (production-us-east)</span>
              <span className="text-xs text-green-400 font-mono">● Safe Mode</span>
            </div>
            <div className="p-4 font-mono text-xs space-y-3">
              <div className="flex items-center space-x-2">
                <span className="text-purple-400 font-bold">&gt;</span>
                <span className="text-slate-100 font-semibold">Why is the payment service returning 502 in staging?</span>
              </div>
              <div className="pl-4 border-l-2 border-slate-800 space-y-2 text-slate-300">
                <p className="text-slate-400">⚡ <span className="text-blue-400">$ kubectl get pods -l app=payment -n staging</span></p>
                <p className="text-amber-400">↳ payment-api-7d9b is CrashLoopBackOff (exit code: 137 / restarts: 8)</p>
                <p className="text-slate-400">🔍 <span className="text-blue-400">$ kubectl logs payment-api-7d9b --previous --tail=20</span></p>
                <p className="text-red-400">↳ FATAL: Out of Memory (OOMKilled limit 256Mi)</p>
              </div>
              <div className="p-3 bg-purple-950/40 border border-purple-800/40 rounded-lg text-slate-200">
                <p className="text-purple-300 font-semibold mb-1">💡 Root Cause & Recommended Action:</p>
                <p className="text-slate-300 text-xs">Pod was killed due to memory limit exhaustion. Increase memory limit to 512Mi:</p>
                <code className="text-green-300 text-[11px] block mt-1">kubectl set resources deployment payment --limits=memory=512Mi -n staging</code>
              </div>
            </div>
          </div>

          {/* 3 Core Value Props */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-800/80 space-y-2">
              <ShieldCheckIcon className="h-6 w-6 text-green-400" />
              <h4 className="font-semibold text-sm text-slate-100">100% Zero-Trust</h4>
              <p className="text-xs text-slate-400">Runs locally on your laptop. Kubeconfigs and certs never leave your machine.</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-800/80 space-y-2">
              <ArrowPathIcon className="h-6 w-6 text-purple-400" />
              <h4 className="font-semibold text-sm text-slate-100">Agentic SRE Loop</h4>
              <p className="text-xs text-slate-400">Auto-drills into logs, events, and metrics to find the true root cause in 1-click.</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-800/80 space-y-2">
              <CommandLineIcon className="h-6 w-6 text-blue-400" />
              <h4 className="font-semibold text-sm text-slate-100">CLI & Desktop App</h4>
              <p className="text-xs text-slate-400">Use native <code className="text-blue-300">kubectl chat</code> or the visual desktop workspace.</p>
            </div>
          </div>
        </div>

        {/* Right column: Early Access Waitlist Card */}
        <div className="lg:col-span-5">
          <div className="p-8 rounded-2xl bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-slate-800 shadow-2xl backdrop-blur-xl space-y-6">
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-white tracking-tight">Get Early Access</h3>
              <p className="text-sm text-slate-400">
                Join 1,000+ DevOps engineers & developers on the VIP release list for the CLI & Desktop app.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Alex Mercer"
                  className="w-full px-3.5 py-2.5 bg-slate-950/80 border border-slate-800 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                  Work / Personal Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="alex@company.com"
                  className="w-full px-3.5 py-2.5 bg-slate-950/80 border border-slate-800 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                  Primary Usage Preference
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-slate-950/80 border border-slate-800 rounded-lg text-sm text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                >
                  <option value="cli">💻 kubectl-chat CLI Plugin</option>
                  <option value="desktop">🖥️ Desktop Visual Workspace</option>
                  <option value="both">🚀 Both CLI & Desktop</option>
                  <option value="enterprise">🏢 Team / Enterprise Cluster</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                  What cluster challenges do you face? (Optional)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="2"
                  placeholder="e.g. Debugging crashloops in staging, complex jsonpaths..."
                  className="w-full px-3.5 py-2.5 bg-slate-950/80 border border-slate-800 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold rounded-lg shadow-lg shadow-purple-500/25 transition duration-200 disabled:opacity-50 text-sm"
              >
                {isSubmitting ? 'Joining VIP list...' : 'Claim Early Access'}
              </button>
            </form>

            {submitMessage && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-xs flex items-center space-x-2">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                <span>{submitMessage}</span>
              </div>
            )}

            <div className="pt-2 border-t border-slate-800/80 text-center">
              <p className="text-xs text-slate-400">
                🔒 No spam. Free community tier included on launch.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 max-w-6xl mx-auto w-full px-6 py-6 border-t border-slate-900 text-center text-xs text-slate-400">
        <p>© 2026 Talk To Your K8s. Built for Kubernetes engineers & developers worldwide.</p>
      </footer>
    </div>
  );
};

export default App;